/** FE-2286: persist Live / Completed / All system view across shipment-detail navigation. */
export const PERSIST_SYSTEM_VIEW_GATE = 'fe-2286-persist-system-view'

const SESSION_STORAGE_KEY = 'command-center-persisted-system-view'
export const ORG_COMMAND_CENTER_VIEWS_SESSION_KEY = 'command-center-views-by-org'
export const ORG_COMMAND_CENTER_VIEWS_LOCAL_KEY = 'command-center-views-by-org'
export const COMMAND_CENTER_ACTIVE_ORG_KEY = 'command-center-active-org-id'
const LOCAL_VIEW_TYPE_KEY = 'command-center'

/**
 * @param {object | null | undefined} statsigClient
 * @returns {boolean} Whether shipment-detail return should restore the active system view.
 */
export const isPersistSystemViewEnabled = (statsigClient) =>
  Boolean(statsigClient?.checkGate?.(PERSIST_SYSTEM_VIEW_GATE))

/**
 * @param {object | null | undefined} viewRef
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @returns {boolean} Whether the view reference matches a system view in the list.
 */
export function isCommandCenterSystemView(viewRef, systemViewsList) {
  if (!viewRef || !Array.isArray(systemViewsList) || systemViewsList.length === 0) {
    return false
  }

  const idLower = String(viewRef?.id ?? '').toLowerCase()
  const nameLower = String(viewRef?.name ?? '')
    .trim()
    .toLowerCase()

  return systemViewsList.some((view) => {
    if (!view) return false
    const viewIdLower = String(view.id ?? '').toLowerCase()
    const viewNameLower = String(view.name ?? '')
      .trim()
      .toLowerCase()
    return (idLower && viewIdLower === idLower) || (nameLower && viewNameLower === nameLower)
  })
}

/**
 * @param {object | null | undefined} viewRef
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @returns {{ id: string, name: string } | null} Matched system view, or null when not found.
 */
export function findCommandCenterSystemView(viewRef, systemViewsList) {
  if (!viewRef || !Array.isArray(systemViewsList) || systemViewsList.length === 0) {
    return null
  }

  const idLower = String(viewRef?.id ?? '').toLowerCase()
  const nameLower = String(viewRef?.name ?? '')
    .trim()
    .toLowerCase()

  const match = systemViewsList.find((view) => {
    if (!view) return false
    const viewIdLower = String(view.id ?? '').toLowerCase()
    const viewNameLower = String(view.name ?? '')
      .trim()
      .toLowerCase()
    return (idLower && viewIdLower === idLower) || (nameLower && viewNameLower === nameLower)
  })

  if (!match) return null
  return { id: match.id, name: match.name }
}

/**
 * @param {object | null | undefined} selectedFilterObj
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @param {object} [options]
 * @param {boolean} [options.preserveSystemAllView] When true, keep the All system view instead of normalizing to null.
 * @returns {object | null} Normalized selected filter, or null for the All view in legacy mode.
 */
export function normalizeCommandCenterSelectedFilter(
  selectedFilterObj,
  systemViewsList,
  { preserveSystemAllView = false } = {},
) {
  if (!selectedFilterObj) return null

  const allSystemMatch = findCommandCenterSystemView(selectedFilterObj, systemViewsList)
  const isAllSystemView =
    allSystemMatch &&
    (String(allSystemMatch.id ?? '').toLowerCase() === 'all' ||
      String(allSystemMatch.name ?? '')
        .trim()
        .toLowerCase() === 'all')

  if (isAllSystemView) {
    return preserveSystemAllView ? allSystemMatch : null
  }

  const idLower = String(selectedFilterObj?.id ?? '').toLowerCase()
  const nameLower = String(selectedFilterObj?.name ?? '')
    .trim()
    .toLowerCase()
  if (idLower === 'all' || nameLower === 'all') {
    return preserveSystemAllView ? selectedFilterObj : null
  }

  return selectedFilterObj
}

/**
 * @returns {{ id: string, name: string } | null} Command-center view from localStorage for the active org, or null.
 * @param {string | null | undefined} [expectedOrgId] When set, ignore cache that belongs to another org.
 */
export function readLocalCommandCenterViewFromStorage(expectedOrgId = null) {
  if (typeof localStorage === 'undefined') return null
  try {
    if (expectedOrgId) {
      const activeOrgId = localStorage.getItem(COMMAND_CENTER_ACTIVE_ORG_KEY)
      if (activeOrgId && activeOrgId !== expectedOrgId) return null
    }
    const raw = localStorage.viewShipmentType
    if (!raw) return null
    const parsed = JSON.parse(raw)?.[LOCAL_VIEW_TYPE_KEY]
    if (!parsed?.id && !parsed?.name) return null
    return { id: parsed.id, name: parsed.name }
  } catch {
    return null
  }
}

/**
 * @returns {Record<string, { id?: string, name?: string }>} Org/user scoped view map from localStorage.
 */
export function readOrgCommandCenterViewMapFromLocalStorage() {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(ORG_COMMAND_CENTER_VIEWS_LOCAL_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeOrgCommandCenterViewMapToLocalStorage(map) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(ORG_COMMAND_CENTER_VIEWS_LOCAL_KEY, JSON.stringify(map))
  } catch {
    // ignore quota / private-mode errors
  }
}

function writeOrgCommandCenterViewMapToSessionStorage(map) {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(ORG_COMMAND_CENTER_VIEWS_SESSION_KEY, JSON.stringify(map))
  } catch {
    // ignore quota / private-mode errors
  }
}

function readMergedOrgCommandCenterViewMap() {
  return {
    ...readOrgCommandCenterViewMapFromLocalStorage(),
    ...readOrgCommandCenterViewMap(),
  }
}

function writeOrgCommandCenterViewMaps(map) {
  writeOrgCommandCenterViewMapToSessionStorage(map)
  writeOrgCommandCenterViewMapToLocalStorage(map)
}

/**
 * Update legacy localStorage cache and active-org marker without writing the org session map.
 * Use when bootstrapping an org default view (not a user-selected view).
 * @param {string | null | undefined} orgId
 * @param {{ id?: string, name?: string } | null | undefined} view
 */
export function writeCommandCenterViewLocalCache(orgId, view) {
  if (!view || (!view.id && !view.name)) return
  if (typeof localStorage === 'undefined') return
  try {
    if (orgId) {
      localStorage.setItem(COMMAND_CENTER_ACTIVE_ORG_KEY, orgId)
    }
    const raw = localStorage.viewShipmentType
    const parsed = raw ? JSON.parse(raw) : {}
    localStorage.viewShipmentType = JSON.stringify({
      ...parsed,
      [LOCAL_VIEW_TYPE_KEY]: { id: view.id, name: view.name },
    })
  } catch {
    // ignore quota / private-mode errors
  }
}

/**
 * Persist the active command-center view for an org in org-scoped storage and the legacy cache.
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} userId
 * @param {{ id?: string, name?: string } | null | undefined} view
 */
export function persistCommandCenterViewForOrg(orgId, userId, view) {
  if (!orgId || !view || (!view.id && !view.name)) return
  writeOrgCommandCenterView(orgId, view, userId)
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(COMMAND_CENTER_ACTIVE_ORG_KEY, orgId)
    const raw = localStorage.viewShipmentType
    const parsed = raw ? JSON.parse(raw) : {}
    localStorage.viewShipmentType = JSON.stringify({
      ...parsed,
      [LOCAL_VIEW_TYPE_KEY]: { id: view.id, name: view.name },
    })
  } catch {
    // ignore quota / private-mode errors
  }
}

/**
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} [userId]
 * @returns {string | null} Session map key scoped by org and optional user.
 */
export function buildCommandCenterViewScopeKey(orgId, userId = null) {
  if (!orgId) return null
  if (userId) return `${orgId}::${userId}`
  return orgId
}

/**
 * @returns {Record<string, { id?: string, name?: string }>} Org/user scoped view map from session storage.
 */
export function readOrgCommandCenterViewMap() {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(ORG_COMMAND_CENTER_VIEWS_SESSION_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} [userId]
 * @returns {{ id: string, name: string } | null} Persisted view for the org/user scope, or null.
 */
export function readOrgCommandCenterView(orgId, userId = null) {
  if (!orgId) return null
  const map = readMergedOrgCommandCenterViewMap()
  const scopedKey = buildCommandCenterViewScopeKey(orgId, userId)
  const scopedView = scopedKey ? map[scopedKey] : null
  if (scopedView?.id || scopedView?.name) {
    return { id: scopedView.id, name: scopedView.name }
  }

  if (userId) {
    const legacyOrgView = map[orgId]
    if (legacyOrgView?.id || legacyOrgView?.name) {
      writeOrgCommandCenterView(orgId, legacyOrgView, userId)
      return { id: legacyOrgView.id, name: legacyOrgView.name }
    }
    return null
  }

  const legacyOrgView = map[orgId]
  if (legacyOrgView?.id || legacyOrgView?.name) {
    return { id: legacyOrgView.id, name: legacyOrgView.name }
  }

  return null
}

/**
 * @param {string | null | undefined} orgId
 * @param {{ id?: string, name?: string } | null | undefined} view
 * @param {string | null | undefined} [userId]
 */
export function writeOrgCommandCenterView(orgId, view, userId = null) {
  const scopeKey = buildCommandCenterViewScopeKey(orgId, userId)
  if (!scopeKey || !view) return
  if (!view.id && !view.name) return

  try {
    const nextMap = {
      ...readMergedOrgCommandCenterViewMap(),
      [scopeKey]: { id: view.id, name: view.name },
    }
    writeOrgCommandCenterViewMaps(nextMap)
  } catch {
    // ignore quota / private-mode errors
  }
}

export function clearOrgCommandCenterViewSession() {
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(ORG_COMMAND_CENTER_VIEWS_SESSION_KEY)
    } catch {
      // ignore
    }
  }
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(ORG_COMMAND_CENTER_VIEWS_LOCAL_KEY)
      localStorage.removeItem(COMMAND_CENTER_ACTIVE_ORG_KEY)
    } catch {
      // ignore
    }
  }
}

/**
 * Remove the stored command-center view for one org/user scope.
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} [userId]
 */
export function clearOrgCommandCenterView(orgId, userId = null) {
  const scopeKey = buildCommandCenterViewScopeKey(orgId, userId)
  if (!scopeKey) return

  try {
    const nextMap = { ...readMergedOrgCommandCenterViewMap() }
    delete nextMap[scopeKey]
    if (userId && nextMap[orgId]) {
      delete nextMap[orgId]
    }
    writeOrgCommandCenterViewMaps(nextMap)
  } catch {
    // ignore
  }
}

/**
 * Snapshot the active command-center view before localStorage is cleared.
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} [userId]
 */
export function snapshotCommandCenterViewForOrg(orgId, userId = null) {
  if (!orgId) return
  const currentView =
    readLocalCommandCenterViewFromStorage(orgId) ?? readOrgCommandCenterView(orgId, userId)
  const viewToPersist = currentView
  if (viewToPersist) {
    persistCommandCenterViewForOrg(orgId, userId, viewToPersist)
  }
}

/**
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} [userId]
 */
export function persistOrgCommandCenterViewFromLocalStorage(orgId, userId = null) {
  if (!orgId) return
  const currentView = readLocalCommandCenterViewFromStorage(orgId)
  if (currentView) {
    writeOrgCommandCenterView(orgId, currentView, userId)
  }
}

/**
 * @returns {{ id: string, name: string } | null} View saved for shipment-detail return navigation.
 */
export function readPersistedCommandCenterSystemView() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.id && !parsed?.name) return null
    return { id: parsed.id, name: parsed.name }
  } catch {
    return null
  }
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} view
 */
export function writePersistedCommandCenterSystemView(view) {
  if (typeof sessionStorage === 'undefined' || !view) return
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id: view.id, name: view.name }))
  } catch {
    // ignore quota / private-mode errors
  }
}

export function clearPersistedCommandCenterSystemView() {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function normalizeCommandCenterViewName(name) {
  return String(name ?? '')
    .trim()
    .toLowerCase()
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} a
 * @param {{ id?: string, name?: string } | null | undefined} b
 * @returns {boolean} Whether both references identify the same command-center view.
 */
export function isSameCommandCenterView(a, b) {
  if (!a || !b) return false
  if (a.id && b.id && a.id === b.id) return true
  const nameA = normalizeCommandCenterViewName(a.name)
  const nameB = normalizeCommandCenterViewName(b.name)
  return Boolean(nameA && nameB && nameA === nameB)
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} viewRef
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @param {Array<{ id?: string, name?: string }> | null | undefined} [customViewsList]
 * @returns {{ id: string, name: string } | null} Matched view from the org catalog, or null.
 */
export function findViewInCommandCenterCatalog(viewRef, systemViewsList, customViewsList = []) {
  if (!viewRef) return null

  const systemMatch = findCommandCenterSystemView(viewRef, systemViewsList)
  if (systemMatch) return systemMatch

  const customViews = Array.isArray(customViewsList) ? customViewsList : []
  if (viewRef.id) {
    const byId = customViews.find((view) => view?.id === viewRef.id)
    if (byId) return byId
  }

  const normalizedName = normalizeCommandCenterViewName(viewRef.name)
  if (normalizedName) {
    const byName = customViews.find(
      (view) => normalizeCommandCenterViewName(view?.name) === normalizedName,
    )
    if (byName) return byName
  }

  return null
}

/**
 * Resolve the view to apply after the org view catalog has been fetched.
 *
 * @param {object} options
 * @param {object | null | undefined} options.bootstrapSelected
 * @param {Array<{ id?: string, name?: string }> | null | undefined} options.systemViewsList
 * @param {Array<{ id?: string, name?: string }> | null | undefined} options.customViewsList
 * @param {string | null | undefined} options.orgId
 * @param {string | null | undefined} options.userId
 * @param {boolean} [options.persistEnabled]
 * @returns {object | null} View to apply after catalog fetch, or null to use org default.
 */
export function resolveCommandCenterEffectiveViewAfterFetch({
  bootstrapSelected,
  systemViewsList,
  customViewsList,
  orgId,
  userId,
  persistEnabled = false,
}) {
  if (!persistEnabled) return bootstrapSelected ?? null
  if (!bootstrapSelected) return null

  const catalogMatch = resolveStoredCommandCenterView(
    bootstrapSelected,
    systemViewsList,
    customViewsList,
  )
  if (catalogMatch) return catalogMatch

  const orgScopedView = readOrgCommandCenterView(orgId, userId)
  if (!orgScopedView || !isSameCommandCenterView(bootstrapSelected, orgScopedView)) {
    return null
  }

  const systemMatch = findCommandCenterSystemView(bootstrapSelected, systemViewsList)
  if (systemMatch) return systemMatch

  return bootstrapSelected
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} viewRef
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @param {Array<{ id?: string, name?: string }> | null | undefined} [customViewsList]
 * @returns {{ id: string, name: string } | null} Resolved view reference, or null when invalid.
 */
export function resolveStoredCommandCenterView(viewRef, systemViewsList, customViewsList = null) {
  if (!viewRef) return null

  const systemMatch = findCommandCenterSystemView(viewRef, systemViewsList)
  if (systemMatch) return systemMatch

  if (Array.isArray(customViewsList)) {
    return findViewInCommandCenterCatalog(viewRef, systemViewsList, customViewsList)
  }

  if (viewRef.id || viewRef.name) {
    return { id: viewRef.id, name: viewRef.name }
  }

  return null
}

/**
 * Persist the active command-center tab to the org/user session map.
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} userId
 * @param {string | null | undefined} viewId
 * @param {string | null | undefined} viewName
 */
export function syncCommandCenterViewSelection(orgId, userId, viewId, viewName) {
  if (!orgId || !userId || (!viewId && !viewName)) return
  persistCommandCenterViewForOrg(orgId, userId, { id: viewId, name: viewName })
}

/**
 * Resolve the command-center view to restore on list bootstrap.
 *
 * @param {object} options
 * @param {object | null | undefined} options.selectedFilterObj
 * @param {Array<{ id?: string, name?: string }> | null | undefined} options.systemViewsList
 * @param {boolean} [options.persistEnabled]
 * @param {string | null | undefined} [options.orgId]
 * @param {string | null | undefined} [options.userId]
 * @param {Array<{ id?: string, name?: string }> | null | undefined} [options.customViewsList]
 * @param {boolean} [options.ignoreLocalSelectedFilter] Skip explicit/local selectedFilterObj (e.g. after org switch).
 * @param {boolean} [options.preferSelectedFilter] Return selectedFilterObj before session/org stored views (explicit user intent).
 * @returns {object | null} View to restore on bootstrap, or null to load the org default.
 */
export function resolveCommandCenterBootstrapSelectedFilter({
  selectedFilterObj,
  systemViewsList,
  persistEnabled = false,
  orgId = null,
  userId = null,
  customViewsList = null,
  ignoreLocalSelectedFilter = false,
  preferSelectedFilter = false,
}) {
  if (!persistEnabled) {
    return normalizeCommandCenterSelectedFilter(selectedFilterObj, systemViewsList)
  }

  // Explicit intent (e.g. just created a view): the caller's selection beats stored state.
  if (preferSelectedFilter && selectedFilterObj) {
    clearPersistedCommandCenterSystemView()
    return normalizeCommandCenterSelectedFilter(selectedFilterObj, systemViewsList, {
      preserveSystemAllView: true,
    })
  }

  const persisted = readPersistedCommandCenterSystemView()
  const persistedMatch = resolveStoredCommandCenterView(persisted, systemViewsList, customViewsList)
  if (persistedMatch) {
    clearPersistedCommandCenterSystemView()
    return persistedMatch
  }

  const orgView = readOrgCommandCenterView(orgId, userId)
  const orgMatch = resolveStoredCommandCenterView(orgView, systemViewsList, customViewsList)
  if (orgMatch) {
    return orgMatch
  }

  if (ignoreLocalSelectedFilter) {
    return null
  }

  if (selectedFilterObj) {
    return normalizeCommandCenterSelectedFilter(selectedFilterObj, systemViewsList, {
      preserveSystemAllView: true,
    })
  }

  return null
}

/**
 * Pylon 1470: resolve the user's default system view (`command-default`, else Live) so a fresh
 * login fetches with the same filters as clicking that tab, not the hardcoded fallback filters.
 *
 * @param {string | null | undefined} commandDefaultKey - `default_view_id['command-default']`.
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @returns {string | null} System view id, or null when the system views are not loaded.
 */
function resolveDefaultSystemViewId(commandDefaultKey, systemViewsList) {
  const keys = commandDefaultKey ? [commandDefaultKey, 'Live'] : ['Live']
  for (const key of keys) {
    const match = findCommandCenterSystemView({ id: key, name: key }, systemViewsList)
    if (match?.id) return match.id
  }
  return null
}

/**
 * Pylon 1470: a saved `command-center` default can point at a view that is no longer in the
 * catalog (e.g. a legacy `shipment_views` id from before the views-table migration). The tab
 * label already skips such ids, so the fetch must skip them too.
 *
 * @param {string} viewId
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @param {Array<{ id?: string }> | null | undefined} customViewsList - Not yet loaded when not an array.
 * @returns {boolean} Whether the id can be used for the shipment list RPC.
 */
function isUsableCommandCenterViewId(viewId, systemViewsList, customViewsList) {
  if (!Array.isArray(customViewsList)) return true
  if (findCommandCenterSystemView({ id: viewId }, systemViewsList)) return true
  return customViewsList.some((view) => view?.id === viewId)
}

/**
 * Resolve the command-center view id for shipment list RPC calls.
 * When persistence is enabled, uses org/user session map then org default — not localStorage.
 * A stored view id and a saved custom default are used only when each is in `customViewsList`
 * (once loaded) or is a system view; otherwise falls back to the default system view when
 * `systemViewsList` is provided.
 *
 * @param {object} options
 * @param {boolean} [options.persistEnabled]
 * @param {string | null | undefined} [options.orgId]
 * @param {string | null | undefined} [options.userId]
 * @param {{ 'command-center'?: string | null, 'command-default'?: string | null } | null | undefined} [options.defaultViewId]
 * @param {Array<{ id?: string, name?: string }> | null | undefined} [options.systemViewsList]
 * @param {Array<{ id?: string }> | null | undefined} [options.customViewsList] - Views this user created. Not yet loaded when not an array.
 * @returns {string | null} Active view id for RPC calls, or null when unset.
 */
export function resolveCommandCenterActiveViewId({
  persistEnabled = false,
  orgId = null,
  userId = null,
  defaultViewId = null,
  systemViewsList = null,
  customViewsList = null,
} = {}) {
  const storedView = persistEnabled
    ? readOrgCommandCenterView(orgId, userId)
    : readLocalCommandCenterViewFromStorage()
  if (
    storedView?.id &&
    isUsableCommandCenterViewId(storedView.id, systemViewsList, customViewsList)
  ) {
    return storedView.id
  }
  const customDefaultViewId = defaultViewId?.['command-center']
  if (
    customDefaultViewId &&
    isUsableCommandCenterViewId(customDefaultViewId, systemViewsList, customViewsList)
  ) {
    return customDefaultViewId
  }
  return resolveDefaultSystemViewId(defaultViewId?.['command-default'], systemViewsList)
}

/**
 * Persist the active command-center view before navigating to shipment details.
 *
 * @param {{ id?: string, name?: string } | null | undefined} view
 * @param {Array<{ id?: string, name?: string }> | null | undefined} systemViewsList
 * @param {boolean} [persistEnabled]
 * @param {string | null | undefined} [orgId]
 * @param {string | null | undefined} [userId]
 */
export function persistCommandCenterSystemViewForShipmentNavigation(
  view,
  systemViewsList,
  persistEnabled,
  orgId = null,
  userId = null,
) {
  if (!persistEnabled) return

  const resolvedView = resolveStoredCommandCenterView(view, systemViewsList)
  if (!resolvedView) return

  writePersistedCommandCenterSystemView(resolvedView)
  if (orgId) {
    writeOrgCommandCenterView(orgId, resolvedView, userId)
  }
}
